import type { MatchAngaben, SifaMitarbeiter } from './types';

/**
 * Abgleichregel „3 von 4" (docs/sifa-unterweisung-plan.md §4) — die
 * TS-Fassung fuer die VORSCHAU im Mitarbeiter-Dialog.
 *
 * Die Regel steht ZWEIMAL: hier und in der Datenbank
 * (`sifa_mitarbeiter_match`, supabase/sifa_unterweisungen_rpc.sql). Beim
 * Abschluss einer Teilnahme entscheidet die SQL-Fassung; diese hier zeigt dem
 * Innendienst vorab, wen ein Datensatz treffen wuerde. Wer eine der beiden
 * aendert, aendert die andere mit — sonst sieht der Dialog eine andere
 * Person als die Datenbank spaeter zuordnet.
 *
 * Felder: Vorname, Nachname, Geburtsdatum, E-Mail. Ein Feld zaehlt nur, wenn
 * es auf BEIDEN Seiten gefuellt ist; Treffer = mindestens 3 gleiche Felder.
 * Mehrere Treffer: 4/4 gewinnt, sonst der juengste `updatedAt`; bei
 * Gleichstand KEIN automatisches Zuordnen (Innendienst entscheidet).
 *
 * Reine Funktionen, keine Datenbank — das Projekt hat keine DOM-Testumgebung,
 * und diese Regel muss ohne Supabase pruefbar sein.
 */

/** Ab so vielen gleichen Feldern gilt eine Person als dieselbe. */
export const MINDEST_TREFFER = 3;

/** Alle vier Felder gleich — schlaegt jeden 3/4-Treffer. */
export const VOLLTREFFER = 4;

// ─── Normalisierung ─────────────────────────────────────────────────────────

/**
 * Grundnormalisierung fuer Namen und freie Texte: trim, Kleinschreibung,
 * ß→ss, Umlaute→ae/oe/ue, Mehrfach-Leerzeichen zu einem.
 *
 * Umlaute werden BUCHSTABENWEISE ersetzt und nicht ueber `normalize('NFD')`
 * entfernt: „Müller" und „Mueller" sind derselbe Name, „Muller" ist es nicht.
 * Ein Teilnehmer tippt am Handy gern die ae-Schreibweise, im Stamm steht die
 * mit Umlaut — beide muessen aufeinander treffen.
 */
export const normalisiereText = (wert?: string | null): string =>
  (wert ?? '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * NACHNAME: wie normalisiereText, zusaetzlich ist ein Bindestrich einem
 * Leerzeichen gleichwertig („Müller-Lüdenscheid" = „Mueller Luedenscheid").
 *
 * Gilt bewusst NUR fuer den Nachnamen — dieselbe Formel wie `sifa_norm(text,
 * true)` in supabase/sifa_unterweisungen_rpc.sql (Nr. 83). Die SQL-Fassung
 * entscheidet beim Abschluss, diese hier ist die Vorschau im Dialog; beide
 * muessen zeichengleich rechnen, sonst schlaegt der Dialog eine Person vor,
 * die der Abschluss dann nicht findet. Der Vorname laeuft ueber
 * normalisiereText („Karl-Heinz" bleibt von „Karl Heinz" verschieden).
 */
export const normalisiereName = (wert?: string | null): string =>
  normalisiereText((wert ?? '').replace(/-/g, ' '));

/** E-Mail: trim + Kleinschreibung. Keine Umlautersetzung — eine Adresse ist, was sie ist. */
export const normalisiereEmail = (wert?: string | null): string => (wert ?? '').trim().toLowerCase();

/**
 * Geburtsdatum auf `YYYY-MM-DD`.
 *
 * Nimmt ISO (auch mit Zeitanteil, es zaehlt nur der Tag) und die deutsche
 * Schreibweise `TT.MM.JJJJ`. Bewusst per Zeichenkette und nie ueber
 * `new Date(...)`: ein reines Tagesdatum wuerde dort als UTC-Mitternacht
 * gelesen und in einer Zeitzone westlich von Greenwich um einen Tag kippen.
 * Was sich nicht erkennen laesst, wird getrimmt zurueckgegeben — dann trifft
 * es eben nur seinesgleichen.
 */
export const normalisiereDatum = (wert?: string | null): string => {
  const text = (wert ?? '').trim();
  if (!text) return '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(text);
  if (de) return `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
  return text;
};

/** Die vier Felder in Vergleichsform. Leer = nicht gefuellt. */
export interface NormalisierteAngaben {
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  email: string;
}

export const normalisiereAngaben = (a: MatchAngaben | null | undefined): NormalisierteAngaben => ({
  vorname: normalisiereText(a?.vorname),
  nachname: normalisiereName(a?.nachname),
  geburtsdatum: normalisiereDatum(a?.geburtsdatum),
  email: normalisiereEmail(a?.email),
});

const FELDER: ReadonlyArray<keyof NormalisierteAngaben> = ['vorname', 'nachname', 'geburtsdatum', 'email'];

// ─── Vergleich ──────────────────────────────────────────────────────────────

/**
 * Anzahl der uebereinstimmenden Felder (0..4).
 *
 * Ein Feld zaehlt NUR, wenn es auf beiden Seiten gefuellt ist. Zwei leere
 * E-Mail-Felder sind keine Uebereinstimmung — sonst waeren zwei Personen mit
 * gleichem Nachnamen und beidseits fehlender E-Mail schon „2 von 4", und mit
 * einem zufaellig gleichen Vornamen dieselbe Person.
 */
export const matchScore = (a: MatchAngaben, b: MatchAngaben): number => {
  const na = normalisiereAngaben(a);
  const nb = normalisiereAngaben(b);
  let score = 0;
  for (const feld of FELDER) {
    if (na[feld] && nb[feld] && na[feld] === nb[feld]) score++;
  }
  return score;
};

export type MatchGrund = 'eindeutig' | 'vier_von_vier' | 'juengster' | 'mehrdeutig' | 'keiner';

export interface MatchErgebnis {
  /** Die automatisch zugeordnete Person — null bei Gleichstand oder ohne Treffer. */
  treffer: SifaMitarbeiter | null;
  /**
   * Alle Personen mit mindestens 3 gleichen Feldern, beste zuerst (Score,
   * dann juengstes updatedAt). Bei `mehrdeutig` die Auswahl fuer den
   * Innendienst; sonst zur Anzeige „so sicher ist der Treffer".
   */
  kandidaten: SifaMitarbeiter[];
  grund: MatchGrund;
}

/** Zeitwert von `updatedAt`; unlesbar = 0 (aeltester denkbarer Stand). */
const zeitwert = (iso?: string | null): number => {
  const t = Date.parse(iso ?? '');
  return Number.isFinite(t) ? t : 0;
};

/**
 * Findet die Person zu den Angaben im Stamm — nach §4.
 *
 * Zeilen mit `zusammengefuehrtIn` sind Dubletten, die in eine andere Zeile
 * aufgegangen sind; sie treffen nie. Die Zielzeile steht ohnehin im Bestand.
 *
 * Bei MEHREREN Treffern: gibt es genau eine Person mit 4/4, ist sie es. Gibt
 * es mehrere 4/4 oder nur 3/4-Treffer, gewinnt unter den besten der juengste
 * `updatedAt` — aber nur, wenn er STRENG juenger ist als der naechste. Bei
 * Gleichstand bleibt `treffer` null: lieber eine Teilnahme ohne
 * Mitarbeiter-Verknuepfung als eine an der falschen Person.
 */
export const findeMitarbeiter = (
  angaben: MatchAngaben,
  bestand: ReadonlyArray<SifaMitarbeiter> | null | undefined,
): MatchErgebnis => {
  const bewertet = (bestand ?? [])
    .filter((m) => m && !m.zusammengefuehrtIn)
    .map((m) => ({ m, score: matchScore(angaben, m), zeit: zeitwert(m.updatedAt) }))
    .filter((x) => x.score >= MINDEST_TREFFER)
    .sort((a, b) => b.score - a.score || b.zeit - a.zeit);

  const kandidaten = bewertet.map((x) => x.m);

  if (bewertet.length === 0) return { treffer: null, kandidaten, grund: 'keiner' };
  if (bewertet.length === 1) return { treffer: bewertet[0].m, kandidaten, grund: 'eindeutig' };

  const bester = bewertet[0];
  const beste = bewertet.filter((x) => x.score === bester.score);

  if (bester.score === VOLLTREFFER && beste.length === 1) {
    return { treffer: bester.m, kandidaten, grund: 'vier_von_vier' };
  }

  // Unter den gleich guten entscheidet der juengste Stand — streng juenger.
  if (beste.length === 1 || beste[0].zeit > beste[1].zeit) {
    return { treffer: beste[0].m, kandidaten, grund: 'juengster' };
  }

  return { treffer: null, kandidaten, grund: 'mehrdeutig' };
};
